import React from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, IconButton, Tooltip, useTheme, useMediaQuery } from '@mui/material';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { useDashboard } from '../../contexts/DashboardContext';
import { toDisplayString } from '../../utils/flowResultUtils';

/**
 * Session summary panel - displays data collected during the chat/flow session.
 * Used when chatPosition is 'header' and center shows collected data.
 */
/** Merge form_prefill with other flow variables (address, payload, etc.) for collected data */
function getCollectedData(structuredOutput) {
  const formPrefill = structuredOutput?.form_prefill && typeof structuredOutput.form_prefill === 'object'
    ? structuredOutput.form_prefill
    : {};
  const skipKeys = new Set([
    'ai_response', 'message', 'table_data', 'chart_data', 'form_prefill', 'executionId', 'status',
    'execution_info_list', 'execution_info', 'metadata', 'rag_context', 'rag_results',
  ]);
  const collected = { ...formPrefill };
  if (structuredOutput && typeof structuredOutput === 'object') {
    for (const [k, v] of Object.entries(structuredOutput)) {
      if (skipKeys.has(k) || v == null) continue;
      if (typeof v === 'object' && !Array.isArray(v) && (k === 'table_data' || k === 'chart_data' || k === 'columns' || k === 'rows')) continue;
      if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
        collected[k] = v;
      } else if (Array.isArray(v) && v.length > 0 && (typeof v[0] !== 'object' || v.length <= 3)) {
        collected[k] = v.length <= 5 ? v : `${v.length} items`;
      } else if (v && typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length <= 5) {
        const val = typeof v.value !== 'undefined' ? v.value : v;
        collected[k] = typeof val === 'object' ? JSON.stringify(val) : val;
      }
    }
  }
  return collected;
}

export default function DashboardSummary() {
  const theme = useTheme();
  const isSFF = useMediaQuery(theme.breakpoints.down('md'));
  const { structuredOutput, lastInput } = useDashboard();

  const tableData = structuredOutput?.table_data;
  const hasTable = tableData?.rows?.length > 0 && Array.isArray(tableData.rows);
  const collectedData = getCollectedData(structuredOutput);
  const hasCollectedData = collectedData && typeof collectedData === 'object' && Object.keys(collectedData).length > 0;
  const aiResponseRaw = structuredOutput?.ai_response || structuredOutput?.message;
  const aiResponse = toDisplayString(aiResponseRaw, '');

  const hasContent = hasTable || hasCollectedData || aiResponse || (lastInput && lastInput.trim());

  if (!hasContent) {
    return (
      <Box sx={{ p: 2, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Session summary will appear here as data is collected from the chat.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, height: '100%', overflowY: 'auto', overflowX: 'hidden' }}>
      {lastInput && lastInput.trim() && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Your query
          </Typography>
          <Typography variant="body2" sx={{ p: 1.5, bgcolor: 'action.selected', borderRadius: 1 }}>
            {lastInput}
          </Typography>
        </Box>
      )}
      {aiResponse && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Response
            </Typography>
            {typeof window !== 'undefined' && window.speechSynthesis && (
              <Tooltip title="Listen">
                <IconButton
                  size="small"
                  onClick={() => {
                    if (window.speechSynthesis) window.speechSynthesis.cancel();
                    const u = new SpeechSynthesisUtterance(aiResponse);
                    window.speechSynthesis.speak(u);
                  }}
                  sx={isSFF ? { minWidth: 44, minHeight: 44 } : {}}
                >
                  <VolumeUpIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
            {aiResponse}
          </Typography>
        </Box>
      )}
      {hasCollectedData && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Collected data
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {Object.entries(collectedData).map(([key, value]) => (
              <Chip
                key={key}
                size="small"
                label={`${key}: ${typeof value === 'object' ? JSON.stringify(value) : String(value ?? '')}`}
                sx={{ fontSize: '0.75rem', maxWidth: '100%' }}
              />
            ))}
          </Box>
        </Box>
      )}
      {hasTable && (
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Table data
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 280 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {(tableData.columns || Object.keys(tableData.rows[0] || {})).map((col) => (
                    <TableCell key={col} sx={{ fontWeight: 600 }}>
                      {typeof col === 'object' ? col?.label ?? col?.id ?? col : col}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {tableData.rows.slice(0, 20).map((row, idx) => (
                  <TableRow key={idx}>
                    {(tableData.columns || Object.keys(row)).map((colKey) => {
                      const key = typeof colKey === 'object' ? colKey?.id ?? colKey?.label : colKey;
                      const val = row[key];
                      return (
                        <TableCell key={key} size="small">
                          {typeof val === 'object' ? JSON.stringify(val) : String(val ?? '')}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {tableData.rows.length > 20 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Showing 20 of {tableData.rows.length} rows
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
